import { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { HelpOutlineOutlined, HistoryOutlined, PeopleOutlined, PlaceOutlined, SensorsOutlined, StoreOutlined, WarningAmberOutlined } from '@mui/icons-material'
import { Box, Button, Divider, Grid, Typography } from '@mui/material'

import { DashboardContext } from '../../providers/dashboard-provider'
import { formatAddress } from '../../utils/helpers'
import useFolderTree from '../../utils/hooks/useFolderTree'
import useLocationSelectModal from '../../utils/hooks/useLocationSelectModal'
import AccountDetailsModal from '../account-details-modal'
import LocationSelectModal from '../location-select-modal'
import Nav from '../nav/nav'

import useStyles from './index.styles'

const Sidenav = () => {
  const classes = useStyles

  const location = useLocation()
  const navigate = useNavigate()
  const { folder, activeAlerts, selectedFolderId } = useContext(DashboardContext)
  const { hasMultipleFolders } = useFolderTree()

  const [showLocationSelectModal, setShowLocationSelectModal] = useLocationSelectModal(selectedFolderId, hasMultipleFolders)
  const [showAccountDetailsModal, setShowAccountDetailsModal] = useState<boolean>(false)

  return (
    <>
      <LocationSelectModal
        open={showLocationSelectModal}
        onClose={() => setShowLocationSelectModal(false)}
        onSelect={(newFolderId) => {
          navigate(`/dashboard/${newFolderId}/devices`)
        }}
        selectedFolderId={folder?.id}
      />

      {folder ? (
        <Grid container={true} sx={classes.container}>
          <Grid container={true} size={12} sx={classes.stickyHeader}>
            <Grid container={true} size={12} sx={classes.viewingHeader}>
              {folder && (
                <Typography variant="body2" sx={classes.viewingText}>
                  Viewing:
                </Typography>
              )}
            </Grid>

            <Grid container={true} size={12} sx={classes.locationContainer}>
              {folder && (
                <Grid container={true} sx={classes.locationDetails}>
                  <Typography variant="subtitle2">{folder.name}</Typography>
                  <Typography variant="overline" sx={classes.addressText}>
                    {formatAddress(folder.information.address)}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {hasMultipleFolders && (
              <Grid container={true} size={12} sx={classes.switchButton}>
                <Button variant="text" size="medium" startIcon={<PlaceOutlined />} onClick={() => setShowLocationSelectModal(true)}>
                  Switch locations
                </Button>
              </Grid>
            )}
          </Grid>

          <Nav.List>
            <Nav.Item
              label="Devices"
              icon={<SensorsOutlined color="secondary" />}
              selected={/\/dashboard\/[^/]+\/devices(?:\/[^/]+)?$/.test(location.pathname)}
              onClick={() => navigate(`/dashboard/${folder.id}/devices`, { state: location.state })}
            />
            <Nav.Item
              label="Alerts"
              icon={<WarningAmberOutlined color="secondary" />}
              selected={location.pathname.includes('/alerts')}
              showBadge={activeAlerts.length > 0}
              onClick={() => navigate(`/dashboard/${folder.id}/alerts`, { state: location.state })}
            />
            <Nav.Item
              label="Contacts"
              icon={<PeopleOutlined color="secondary" />}
              selected={location.pathname.includes('/contacts')}
              onClick={() => navigate(`/dashboard/${folder.id}/contacts`, { state: location.state })}
            />
            <Divider />

            <Nav.Item label="Account Details" icon={<StoreOutlined color="secondary" />} selected={false} onClick={() => setShowAccountDetailsModal(true)} />

            <Nav.Item
              label="Order History"
              icon={<HistoryOutlined color="secondary" />}
              selected={location.pathname.includes('/order-history')}
              onClick={() => navigate(`/dashboard/${folder.id}/order-history`, { state: location.state })}
            />
            <Divider />
            <Nav.Item
              label="Help & Support"
              icon={<HelpOutlineOutlined color="secondary" />}
              selected={location.pathname.includes('/help')}
              onClick={() => navigate(`/dashboard/${folder.id}/help`, { state: location.state })}
            />
          </Nav.List>

          <AccountDetailsModal isOpen={showAccountDetailsModal} onClose={() => setShowAccountDetailsModal(false)} folderId={folder.id} />
        </Grid>
      ) : (
        <Box />
      )}
    </>
  )
}

export default Sidenav
